import React from 'react';
import { Link } from 'react-router-dom';
import { Button, AnimatedBackground } from '../components/common';

const features = [
  {
    icon: '\u{1F4DD}',
    title: 'Grammar Practice',
    description: 'Listen to sentences and practice spelling and grammar',
    color: 'bg-indigo-100',
  },
  {
    icon: '\u{1F4DA}',
    title: 'Reading Stories',
    description: 'Fun stories with comprehension questions',
    color: 'bg-emerald-100',
  },
  {
    icon: '\u{1F3A7}',
    title: 'Listening Skills',
    description: 'Listen carefully and answer questions',
    color: 'bg-amber-100',
  },
  {
    icon: '\u{1F399}',
    title: 'Speaking Practice',
    description: 'Record your voice and get instant feedback',
    color: 'bg-pink-100',
  },
];

const benefits = [
  { icon: '\u{1F3AF}', text: 'Age-appropriate content for 4-12 years' },
  { icon: '\u{1F4CA}', text: 'Adjustable difficulty levels' },
  { icon: '\u{1F3AE}', text: 'Fun and engaging exercises' },
  { icon: '\u{1F4AA}', text: 'AI-powered pronunciation feedback' },
  { icon: '\u2B50', text: 'Track progress and earn achievements' },
  { icon: '\u{1F46A}', text: 'Safe learning environment for kids' },
];

const LandingPage: React.FC = () => {

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-100 via-sky-50 to-kids-bg dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 relative overflow-hidden">
      {/* Animated Background */}
      <AnimatedBackground variant="sky" />


      {/* Header */}
      <header className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm shadow-sm dark:shadow-slate-900 relative z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-4xl">{'\u{1F433}'}</span>
            <span className="text-2xl font-bold text-kids-primary dark:text-indigo-400">Litrlly Kids</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/pricing">
              <Button variant="secondary" size="small">Pricing</Button>
            </Link>
            <Link to="/login">
              <Button variant="primary" size="small">Log In</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-4 relative z-[1]">
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-8xl mb-6 animate-jump-few">
            {'\u{1F433}'}
          </div>
          <h1 className="text-5xl font-bold text-gray-800 dark:text-white mb-4">
            Learn English the <span className="text-kids-primary dark:text-indigo-400">Fun Way!</span>
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
            Interactive lessons designed for kids ages 4-12. Practice grammar, reading, listening, and speaking with AI-powered feedback!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/login">
              <Button variant="primary" size="large">
                Start Learning Free
              </Button>
            </Link>
            <Link to="/pricing">
              <Button variant="secondary" size="large">
                See Pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm relative z-[1]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-800 dark:text-white mb-12">
            4 Fun Ways to Learn! {'\u{1F389}'}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature) => (
              <div
                key={feature.title}
                className={`${feature.color} dark:bg-slate-700 rounded-3xl p-6 text-center transition-transform hover:scale-105`}
              >
                <div className="text-5xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">{feature.title}</h3>
                <p className="text-gray-600 dark:text-gray-300">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4 relative z-[1]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-800 dark:text-white mb-12">
            Why Parents Love Us {'\u2764\uFE0F'}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-white dark:bg-slate-800 rounded-2xl p-4 flex items-center gap-4 shadow-sm dark:shadow-slate-900"
              >
                <span className="text-3xl">{benefit.icon}</span>
                <span className="text-lg text-gray-700 dark:text-gray-200">{benefit.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-16 px-4 bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm relative z-[1]">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-4">
            Simple Pricing {'\u{1F4B0}'}
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-8">
            One plan with everything your child needs to learn English
          </p>
          <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl p-8 text-white">
            <div className="text-lg mb-2">Kids Premium</div>
            <div className="text-5xl font-bold mb-2">$9.99</div>
            <div className="text-indigo-200 mb-6">CAD / month</div>
            <ul className="text-left space-y-3 mb-8">
              <li className="flex items-center gap-2">
                <span>{'\u2705'}</span> Unlimited exercises in all sections
              </li>
              <li className="flex items-center gap-2">
                <span>{'\u2705'}</span> AI-powered pronunciation feedback
              </li>
              <li className="flex items-center gap-2">
                <span>{'\u2705'}</span> Adaptive difficulty levels
              </li>
              <li className="flex items-center gap-2">
                <span>{'\u2705'}</span> Progress tracking for parents
              </li>
            </ul>
            <Link to="/pricing">
              <Button variant="secondary" size="large" fullWidth>
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 relative z-[1]">
        <div className="max-w-2xl mx-auto text-center">
          <div className="text-6xl mb-4">{'\u{1F31F}'}</div>
          <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-4">
            Ready to Start Learning?
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-8">
            Join thousands of kids improving their English every day!
          </p>
          <Link to="/login">
            <Button variant="primary" size="large">
              Create Free Account
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8 px-4 relative z-[1]">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            {/* Brand */}
            <div className="text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                <span className="text-2xl">{'\u{1F433}'}</span>
                <span className="text-xl font-bold">Litrlly Kids</span>
              </div>
              <p className="text-gray-400 text-sm">
                Making English learning fun for kids everywhere
              </p>
            </div>

            {/* Legal Links */}
            <div className="flex items-center gap-6 text-sm">
              <a
                href="https://litrlly.app/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Privacy Policy
              </a>
              <a
                href="https://litrlly.app/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Terms of Service
              </a>
              <a
                href="https://litrlly.app/contact"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Contact
              </a>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-700 text-center">
            <p className="text-gray-500 text-xs">
              &copy; {new Date().getFullYear()} Litrlly. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
